export const BASE = {
  sf: 40000,
  rent: 36,
  occ: 93,
  other: 100000,
  exp: 15,
  groundRent: 0,
  cap: 7.0,
  tdc: 12950000,
  debtPct: 65,
  equityPct: 35,
  ltv: 60,
  saleCost: 2,
  refiCost: 1,
  itemsControl: [
    { id: "c1", label: "Public-private / land-use attorney + Exclusive Negotiating Agreement work", amount: 75000, cat: "legal" },
    { id: "c2", label: "Concept refinement / partner package", amount: 50000, cat: "leasing" },
    { id: "c3", label: "Financial model / market research", amount: 25000, cat: "leasing" },
    { id: "c4", label: "Survey / title / initial due diligence", amount: 25000, cat: "legal" },
    { id: "c5", label: "Sponsor development management", amount: 50000, cat: "leasing" },
    { id: "c6", label: "Early reserve", amount: 25000, cat: "contingency" },
  ],
  itemsPad: [
    { id: "p1", label: "Architecture / site planning", amount: 150000, cat: "ae" },
    { id: "p2", label: "Civil engineering / utilities / traffic", amount: 140000, cat: "ae" },
    { id: "p3", label: "Environmental review", amount: 150000, cat: "legal" },
    { id: "p4", label: "Entitlement legal / application fees", amount: 90000, cat: "legal" },
    { id: "p5", label: "Leasing validation / branding / placemaking validation", amount: 50000, cat: "leasing" },
    { id: "p6", label: "Stage contingency", amount: 70000, cat: "contingency" },
  ],
  itemsBuildReady: [
    { id: "b1", label: "Construction documents", amount: 300000, cat: "ae" },
    { id: "b2", label: "Permit / plan-check fees", amount: 80000, cat: "ae" },
    { id: "b3", label: "General Contractor preconstruction / bidding", amount: 75000, cat: "ae" },
    { id: "b4", label: "Leasing legal / tenant coordination", amount: 75000, cat: "legal" },
    { id: "b5", label: "Geotechnical work / technical studies", amount: 70000, cat: "ae" },
    { id: "b6", label: "Financing preparation / closing preparation", amount: 100000, cat: "finance" },
    { id: "b7", label: "Stage contingency", amount: 150000, cat: "contingency" },
  ],
  priceControl: null,
  pricePad: null,
  priceBuildReady: null,
  partnerCapPct: 90,
  prefRate: 8,
  parkingStalls: 215,
  courtyardSf: 60000,
  jvSplit: 50,
  mControl: 6,
  mPad: 18,
  mBuildReady: 30,
  mSell: 54,
  mHold: 60,
};

export const PARCELS = [
  { apn: "906-171-001", acres: 2.72 },
  { apn: "906-171-005", acres: 9.9 },
];
export const SITE_SF = 549727;
export const SITE_ACRES = 12.62;

export const BUDGET = [
  { key: "shell", label: "Building Shell / Core + Landlord MEP", amount: 6600000, basis: "≈ 40,000 SF × $165/SF conceptual shell/core assumption" },
  { key: "infra", label: "Shared Market-Hall / Restaurant Infrastructure", amount: 1000000, basis: "Common utility capacity, grease, market-hall and shared restaurant infrastructure. Tenant-specific interiors and equipment excluded." },
  { key: "site", label: "Site / Civil / Utilities / Parking", amount: 900000, basis: "On-site infrastructure and sitework working allowance" },
  { key: "courtyard", label: "Courtyard / Landscape / Play / Water / Stage", amount: 1100000, basis: "Signature public realm and family amenities" },
  { key: "ae", label: "Architecture / Engineering / Permits", amount: 950000, basis: "Design, civil, consultants, permitting and plan check" },
  { key: "legal", label: "Legal / Environmental / Survey / Title / Predevelopment", amount: 450000, basis: "Public-private transaction, diligence and environmental pathway" },
  { key: "leasing", label: "Leasing / Branding / Project Management", amount: 350000, basis: "Leasing strategy, placemaking, project management" },
  { key: "contingency", label: "Contingency", amount: 750000, basis: "Concept-stage allowance; to be reset after contractor pricing" },
  { key: "finance", label: "Financing Carry / Closing", amount: 850000, basis: "Working construction financing and closing allowance" },
];

export const BUDGET_COLORS = ["#17324D", "#2D6E6A", "#3F8A84", "#5E9B96", "#2C3944", "#4A6B82", "#7B8794", "#9FB7B4", "#C5D3D9"];

const RESERVE = { control: 0.35, pad: 0.3, buildReady: 0.25 };
const FRICTION = { control: 25000, pad: 50000, buildReady: 75000 };

export const ITEM_KEYS = ["itemsControl", "itemsPad", "itemsBuildReady"];
const sum = (arr) => arr.reduce((t, v) => t + v.amount, 0);
const byCat = (src) => {
  const out = {};
  ITEM_KEYS.forEach((k) => src[k].forEach((it) => { out[it.cat] = (out[it.cat] || 0) + it.amount; }));
  return out;
};

export function withStageCosts(i) {
  const controlCost = sum(i.itemsControl);
  const padCost = controlCost + sum(i.itemsPad);
  const buildReadyCost = padCost + sum(i.itemsBuildReady);
  const predevDelta = buildReadyCost - ITEM_KEYS.reduce((t, k) => t + sum(BASE[k]), 0);
  const now = byCat(i), base = byCat(BASE);
  const budgetDelta = {};
  BUDGET.forEach((b) => { budgetDelta[b.key] = (now[b.key] || 0) - (base[b.key] || 0); });
  return { ...i, controlCost, padCost, buildReadyCost, predevDelta, budgetDelta, tdcInput: i.tdc, tdc: i.tdc + predevDelta };
}

export function compute(a) {
  const p = (v) => v / 100;
  const scheduled = a.sf * a.rent;
  const occupied = scheduled * p(a.occ);
  const vacancy = scheduled - occupied;
  const effective = occupied + a.other;
  const expenses = effective * p(a.exp);
  const noiPre = effective - expenses;
  const noi = noiPre - a.groundRent;
  const value = a.cap > 0 ? noi / p(a.cap) : 0;
  const valueAt = (cap, n = noi) => n / p(cap);
  const debt = a.tdc * p(a.debtPct);
  const equity = a.tdc * p(a.equityPct);
  const saleCosts = value * p(a.saleCost);
  const netSale = value - saleCosts;
  const profit = netSale - a.tdc;
  const poc = a.tdc ? profit / a.tdc : 0;
  const refiLoan = value * p(a.ltv);
  const refiCosts = refiLoan * p(a.refiCost);
  const cashOut = refiLoan - debt - refiCosts;
  const retained = value - refiLoan;
  const perSf = a.sf ? a.tdc / a.sf : 0;

  const stage = (key, cum, override) => {
    const remaining = Math.max(a.tdc - cum, 0);
    const reserve = remaining * RESERVE[key];
    const modeled = Math.max(value - remaining - reserve, 0);
    const assignment = override ?? modeled;
    return { cum, remaining, reserve, reservePct: RESERVE[key], friction: FRICTION[key], modeled, overridden: override != null, assignment, created: assignment - cum - FRICTION[key] };
  };
  const control = stage("control", a.controlCost, a.priceControl);
  const pad = stage("pad", a.padCost, a.pricePad);
  const buildReady = stage("buildReady", a.buildReadyCost, a.priceBuildReady);

  const noiAtRent = (r) => ((a.sf * r * p(a.occ) + a.other) * (1 - p(a.exp))) - a.groundRent;

  const footprint = a.sf * 1.1;
  const stalls = Math.round(a.parkingStalls);
  const ratio = a.sf ? stalls / (a.sf / 1000) : 0;
  const parkingSf = stalls * 350;
  const circulation = SITE_SF * 0.1;
  const utilities = 25000;
  const used = footprint + a.courtyardSf + parkingSf + circulation + utilities;
  const site = {
    stalls,
    ratio,
    rows: [
      { key: "building", label: "Building footprint", sf: footprint, basis: `${a.sf.toLocaleString()} rentable SF × 1.10 gross-up · single-story perimeter market hall`, color: "#17324D" },
      { key: "courtyard", label: "Central courtyard / play / lawn / stage", sf: a.courtyardSf, basis: "Conceptual open-air family courtyard and event lawn", color: "#2D6E6A" },
      { key: "parking", label: "Surface parking", sf: parkingSf, basis: `${stalls} stalls (≈ ${ratio.toFixed(2)} / 1,000 SF) × ~350 SF per stall incl. drive aisles`, color: "#4A6B82" },
      { key: "circulation", label: "Access drives / landscape / setbacks", sf: circulation, basis: "≈ 10% of gross site area — working allowance", color: "#7B8794" },
      { key: "utilities", label: "Stormwater / utilities / service", sf: utilities, basis: "Retention, trash enclosures, loading — working allowance", color: "#9FB7B4" },
      { key: "reserve", label: "Remaining land — future phase / expansion reserve", sf: Math.max(SITE_SF - used, 0), basis: "Not underwritten · potential future pads, overflow parking or open space", color: "#D8E0E5" },
    ],
    coverage: footprint / SITE_SF,
    over: used > SITE_SF,
  };

  return {
    site,
    scheduled, occupied, vacancy, effective, expenses, noiPre, noi, value, valueAt, noiAtRent,
    debt, equity, saleCosts, netSale, profit, poc, refiLoan, refiCosts, cashOut, retained, perSf,
    budgetScale: (a.tdcInput ?? a.tdc) / 12950000,
    stages: { control, pad, buildReady },
    holdCreated: value - a.tdc - refiCosts,
  };
}

export const fmtM = (v, d = 2) => `${v < 0 ? "−" : ""}$${(Math.abs(v) / 1e6).toFixed(d)}M`;
export const fmtK = (v) => `${v < 0 ? "−" : ""}$${Math.round(Math.abs(v) / 1000).toLocaleString()}K`;
export const fmtMoney = (v) => (v === 0 ? "$0" : Math.abs(v) >= 1e6 ? fmtM(v) : fmtK(v));
export const fmtUsd = (v) => `${v < 0 ? "−" : ""}$${Math.round(Math.abs(v)).toLocaleString()}`;
export const fmtPct = (v, d = 2) => `${Number(v).toFixed(d)}%`;
